import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { JSX, useContext, useRef, useState } from "react"
import { ProviderAccordionSelectedTab } from "@app/projects/network-assessment/maps/network-coverage-map/enumerators";
import { createPortal } from "react-dom";
import { useMapboxControl } from "@app/shared/mapping/useMapBoxControl";
import { NetworkCoverageMapDataContext } from "@app/projects/network-assessment/maps/network-coverage-map/contexts/NetworkCoverageMapDataContext";
import { ProviderAccordionControlHeader } from "@app/projects/network-assessment/maps/network-coverage-map/components/ProviderAccordionControlHeader";
import { ProviderAccordionControlContent } from "@app/projects/network-assessment/maps/network-coverage-map/components/ProviderAccordionControlContent";
import { ProviderAccordionControlProps } from "@app/projects/network-assessment/maps/network-coverage-map/interfaces";
import { PROVIDER_ACCORDION_BUTTON_COLOR } from "@app/projects/network-assessment/maps/network-coverage-map/NetworkCoverageMapsConstants";
import NetworkCoverageMapFloatingFilterControl from "@app/projects/network-assessment/maps/network-coverage-map/controls/NetworkCoverageMapFloatingFilterControl";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { QESButton } from "@app/shared/qes-standards/Button";

export const ProviderAccordionControl = ({ updateMapInputs, ignoreInMapExport, position }: ProviderAccordionControlProps): JSX.Element => {
  const mapDataContext = useContext(NetworkCoverageMapDataContext);

  const headerRef = useRef<HTMLDivElement>(null);

  const providerAccordionToggleButtonDefaultStyle = "2px solid transparent";

  const [providerAccordionToggleButtonStyle, setProviderAccordionToggleButtonStyle] = useState<string>(providerAccordionToggleButtonDefaultStyle);

  const toggleAccordion = () => {
    mapDataContext.setIsProviderAccordionOpen(!mapDataContext.isProviderAccordionOpen);
    setProviderAccordionToggleButtonStyle(providerAccordionToggleButtonDefaultStyle);
  }

  const leftPosition = mapDataContext.isProviderAccordionOpen
    ? document.getElementsByClassName("network-coverage-map__provider-accordion-container")[0]?.clientWidth ?? 0
    : 0;

  // Use null coalescing operator to default to 0 if any of the properties
  // in the chain are undefined to prevent NaN values in the calculation
  const buttonElement = document.getElementsByClassName("network-coverage-map__provider-accordion-button")[0];
  const buttonHeight = buttonElement?.clientHeight ?? 0;

  const topPosition =
    (mapDataContext.mapRef.current?.getCanvas().getBoundingClientRect().height / 2) -
    (buttonHeight / 2);

  const container = useMapboxControl({ mapRef: mapDataContext.mapRef, position });
  if (!container) return null;

  return createPortal(
    <>
      <div className="network-coverage-map__provider-accordion" {...(ignoreInMapExport ? { 'data-html2canvas-ignore': '' } : {})}>
        {mapDataContext.isProviderAccordionOpen &&
          <div
            className="network-coverage-map__provider-accordion-container"
            style={{
              height: mapDataContext.mapRef.current?.getCanvas().getBoundingClientRect().height
            }}
          >
            <div ref={headerRef}>
              <div className="tabs-style-2 network-coverage-map__provider-accordion-tabs">
                <ul className="nav nav-tabs nav-fill" role="menu">
                  {mapDataContext.showNetworkProvidersTab &&
                    <li className={`nav-item ${!mapDataContext.showOutOfNetworkProvidersTab ? 'w-100 text-center' : ''}`} role="none">
                      <a
                        className={`nav-link ${mapDataContext.providerAccordionSelectedTab === ProviderAccordionSelectedTab.NetworkProviders ? 'active' : ''}`}
                        onClick={() => mapDataContext.setProviderAccordionSelectedTab(ProviderAccordionSelectedTab.NetworkProviders)}
                        role="menuitem"
                      >
                        Network Providers
                      </a>
                    </li>
                  }
                  {mapDataContext.showOutOfNetworkProvidersTab &&
                    <li className={`nav-item ${!mapDataContext.showNetworkProvidersTab ? 'w-100 text-center' : ''}`} role="none">
                      <a
                        className={`nav-link ${mapDataContext.providerAccordionSelectedTab === ProviderAccordionSelectedTab.OutOfNetworkProviders ? 'active' : ''}`}
                        onClick={() => mapDataContext.setProviderAccordionSelectedTab(ProviderAccordionSelectedTab.OutOfNetworkProviders)}
                        role="menuitem"
                      >
                        <span>
                          Out-Of-Network Providers
                          <TooltipWrapper
                            text="These providers service the area but are not currently in your network"
                            position="right"
                            containerClassName="ms-2"
                            oneLine
                          >
                            <QesIcon
                              iconName={QesIcons.InfoCircle}
                              className="network-coverage-map__provider-accordion-tooltip-icon"
                              color="#828282"
                              height="16"
                              width="16"
                              description="Tooltip"
                            />
                          </TooltipWrapper>
                        </span>
                      </a>
                    </li>
                  }
                </ul>
              </div>
              <ProviderAccordionControlHeader />
            </div>
            <ProviderAccordionControlContent headerRef={headerRef} />
          </div>
        }
      </div >
      <div
        {...(ignoreInMapExport ? { 'data-html2canvas-ignore': '' } : {})}
      >
        {/* Required to dynamically position the button, otherwise Mapbox IControl stacks vertically*/}
        <div
          style={{
            left: leftPosition,
            position: 'absolute',
            top: 0,
          }}
        >
          <QESButton
            qesTestId="network-coverage-map__provider-accordion-update-inputs-button"
            className="network-coverage-map__provider-accordion-update-inputs-button"
            qesType="none"
            onClick={updateMapInputs}
          >
            Update Inputs
          </QESButton>
        </div>
        {/* Required to dynamically position the button, otherwise Mapbox IControl stacks vertically*/}
          <div
            style={{
              left: leftPosition + 135,
              position: 'absolute',
              top: 0,
            }}
          >
            <NetworkCoverageMapFloatingFilterControl/>
          </div>
        {/* Required to dynamically position the button, otherwise Mapbox IControl stacks vertically */}
        <div
          style={{
            left: leftPosition - 16,
            position: 'absolute',
            top: topPosition,
          }}
        >
          <QESButton
            id="network-coverage-map__provider-accordion-button"
            className="network-coverage-map__provider-accordion-button"
            onMouseOut={() => {
              setProviderAccordionToggleButtonStyle(providerAccordionToggleButtonDefaultStyle);
            }}
            onMouseOver={() => {
              setProviderAccordionToggleButtonStyle(`2px solid ${PROVIDER_ACCORDION_BUTTON_COLOR}`);
            }}
            onClick={toggleAccordion}
            qesType="secondary"
            qesTestId="network-coverage-map__provider-accordion-button"
            style={{
              backgroundColor: "white",
              border: providerAccordionToggleButtonStyle,
              textDecoration: 'none',
            }}
          >
            <TooltipWrapper
              key={mapDataContext.isProviderAccordionOpen ? "provider-accordion-open-tooltip" : "provider-accordion-closed-tooltip"}
              text={mapDataContext.isProviderAccordionOpen ? "Close Providers" : "Open Providers"} >
              <QesIcon
                iconName={QesIcons.ChevronRight}
                className="network-coverage-map__provider-accordion-icon"
                color={PROVIDER_ACCORDION_BUTTON_COLOR}
                height="16"
                style={{
                  backgroundColor: 'transparent',
                  padding: '0px',
                  position: 'relative',
                  right: '4px',
                  transform: mapDataContext.isProviderAccordionOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
                testId="network-coverage-map__filter-icon"
                width="16"
                description={mapDataContext.isProviderAccordionOpen ? "Close Providers" : "Open Providers"}
              />
            </TooltipWrapper>
          </QESButton>
        </div>
      </div >
    </>,
    container
  )
}